import React from "react";
import { Box, Typography } from "@mui/material";
import { BatteryIcon } from "../../../Components/Icons/SectionIcons";

// Pieces shared by every pack card, so the Record Replacement and Edit Pack Details
// screens read as one surface rather than two that were styled to look alike.
//
// These were private to PackReplacementCard and were copied into the edit screen when
// it was built. A copy is exactly how the two drift: the copied FieldLabel had already
// picked up a different font size, weight and required-marker treatment before anyone
// noticed. One definition, two consumers.

export const FieldLabel = ({ children, required = false }) => (
    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#494949", mb: "5px" }}>
        {children}
        {required && <span style={{ color: "#E5484D", marginLeft: 2 }}>*</span>}
    </Typography>
);

// The OUTGOING / INCOMING band label: the battery glyph plus the word, in the band's
// own colour. The glyph is the same BatteryIcon the pre-multi-pack form showed in its
// SectionIconTile - at this card's density the 40px tile does not fit, so the icon is
// used bare and inherits the band colour through `color`.
export const BandLabel = ({ text, color }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", pt: "18px" }}>
        <Box sx={{ display: "flex", color, flexShrink: 0 }}>
            <BatteryIcon size={18} />
        </Box>
        <Typography
            sx={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                color,
                whiteSpace: "nowrap",
            }}
        >
            {text}
        </Typography>
    </Box>
);

// The two band shells. Held here rather than inline so a change to the palette or the
// corner radius lands on both screens at once.
export const OUTGOING_BAND_SX = {
    border: "1px solid #ECDCA0",
    backgroundColor: "#FFFBEA",
    borderRadius: "10px",
    padding: "0 18px 16px",
    display: "grid",
    columnGap: "20px",
    alignItems: "start",
};

export const INCOMING_BAND_SX = {
    border: "1px solid #BEE0C7",
    backgroundColor: "#EDF7EF",
    borderRadius: "10px",
    padding: "0 18px 16px",
    display: "grid",
    columnGap: "20px",
    alignItems: "start",
};

export const OUTGOING_BAND_COLOR = "#8A6D0B";
export const INCOMING_BAND_COLOR = "#2E7D32";

// The card shell: the grey header carrying the slot chip and whatever the screen puts
// beside it, then the body the bands sit in.
export const PackCardShell = ({ chipLabel, chipTestId, headerExtra, actions, children }) => (
    <Box
        sx={{
            border: "1px solid #E4E4E4",
            borderRadius: "12px",
            backgroundColor: "#fff",
            mb: 2,
            overflow: "hidden",
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                backgroundColor: "#F9F9FC",
                borderBottom: "1px solid #EFEFEF",
                padding: "12px 18px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box
                    data-testid={chipTestId}
                    sx={{
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#4A4AF4",
                        backgroundColor: "#EEF0FF",
                        borderRadius: "6px",
                        padding: "5px 12px",
                    }}
                >
                    {chipLabel}
                </Box>
                {headerExtra}
            </Box>
            {actions}
        </Box>

        <Box sx={{ position: "relative", padding: "16px 18px 18px" }}>{children}</Box>
    </Box>
);
